import {
  effect,
  EnvironmentProviders,
  inject,
  Injectable,
  makeEnvironmentProviders,
  provideAppInitializer,
  Type
} from '@angular/core'

import { TranslateService } from '@ngx-translate/core'

import { LOCALE_PIN_SOURCE, LocalePinSource } from '../tokens/locale-pin.token'

/**
 * Applies whatever language the app's {@link LocalePinSource} pins, on top of the
 * per-account language `initializeLanguage()` resolved at boot.
 *
 * The pin wins over the account chain by construction: it is applied *after*
 * boot and after login, and re-applied every time the source emits a different
 * value. It does not fight anything else for control though, because it reacts
 * to changes of the *pin*, not of the current language. So a language chosen by
 * some other means after the pin settled (an in-app switcher, say) survives
 * until the pin itself changes, e.g. because the operator moved to a site that
 * speaks something else.
 *
 * The applied pin is mirrored into `localStorage['lang']` so the next boot
 * paints the right language before the source has had time to resolve.
 */
@Injectable({ providedIn: 'root' })
export class LocalePinService {
  private readonly translate = inject(TranslateService)
  private readonly source = inject(LOCALE_PIN_SOURCE, { optional: true })

  /** Last pin this service applied, so we only react to genuine pin changes. */
  private applied: string | null = null

  /**
   * The pin currently in force, or `null` if none has landed yet. Read by the
   * two other places that set a language from the account (boot and login) so
   * they stand down rather than undo the pin behind its back.
   */
  get currentPin(): string | null {
    return this.applied
  }

  constructor() {
    if (!this.source) return

    effect(() => {
      const pin = this.source?.pinnedLang() ?? null
      if (!pin || pin === this.applied) return

      this.applied = pin
      if (this.translate.getCurrentLang() !== pin) {
        this.translate.use(pin)
      }
      if (localStorage.getItem('lang') !== pin) {
        localStorage.setItem('lang', pin)
      }
    })
  }
}

/**
 * Wires a tenant-provided pin source and starts honouring it.
 *
 * ```ts
 * providers: [provideLocalePin(SiteLocaleService)]
 * ```
 */
export const provideLocalePin = (source: Type<LocalePinSource>): EnvironmentProviders =>
  makeEnvironmentProviders([
    { provide: LOCALE_PIN_SOURCE, useExisting: source },
    // Root services are lazy; the pin has to be listening from the start.
    provideAppInitializer(() => {
      inject(LocalePinService)
    })
  ])
